"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { BASE_URL } from "../config/apiConfig";

const SubscriptionContextHome = createContext(undefined);

export const useSubscriptionHome = () => {
  const context = useContext(SubscriptionContextHome);
  if (context === undefined) {
    throw new Error('useSubscriptionHome must be used within a SubscriptionProviderHome');
  }
  return context;
};

export const SubscriptionProviderHome = ({ children }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cart, setCart] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedCart = localStorage.getItem("sub_cart_home");
      return savedCart ? JSON.parse(savedCart) : [];
    }
    return [];
  });

  // Fetch subscription plans on component mount (no authentication required)
  useEffect(() => {
    setLoading(true);
    fetch(`${BASE_URL}/subscriptions_list`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200 && Array.isArray(data.data)) {
          const formatted = data.data.map((plan) => ({
            id: plan.id,
            name: plan.title,
            price: `${plan.amount}`,
            per: "Month",
            features: JSON.parse(plan.features),
            status: plan.status,
          }));
          setPlans(formatted);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Failed to load subscription plans:", err))
      .finally(() => setLoading(false));
  }, []); // Empty dependency array since no token is needed

  const addToCart = (plan) => {
    setCart((prevCart) => {
      if (prevCart.length > 0) {
        alert("You can only have one subscription plan in the cart at a time.");
        return prevCart;
      }
      const updatedPlan = { ...plan, quantity: 1, price: plan.price };
      const updated = [updatedPlan];
      if (typeof window !== 'undefined') {
        localStorage.setItem("sub_cart_home", JSON.stringify(updated));
      }
      return updated;
    });
  };

  const removeFromSubCart = (planId) => {
    setCart((prevCart) => {
      const updated = prevCart.filter((item) => item.id !== planId);
      if (typeof window !== 'undefined') {
        localStorage.setItem("sub_cart_home", JSON.stringify(updated));
      }
      return updated;
    });
  };

  const clearCart = () => {
    setCart([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem("sub_cart_home");
    }
  };

  const updateCartQuantity = (planId, quantity) => {
    if (quantity <= 0) {
      removeFromSubCart(planId);
      return;
    }

    setCart((prevCart) => {
      const updated = prevCart.map((item) =>
        item.id === planId ? { ...item, quantity } : item
      );
      if (typeof window !== 'undefined') {
        localStorage.setItem("sub_cart_home", JSON.stringify(updated));
      }
      return updated;
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <SubscriptionContextHome.Provider
      value={{
        plans,
        loading,
        selectedPlan,
        setSelectedPlan,
        cart,
        addToCart,
        removeFromSubCart,
        clearCart,
        updateCartQuantity,
        getCartTotal,
        getCartItemCount,
      }}
    >
      {children}
    </SubscriptionContextHome.Provider>
  );
};
